/**
 * Line highlights for click-to-source (docs/ui-design.md §3): a flash on the
 * line a timeline row jumped to, and a softer highlight while a row is
 * hovered. Styles live in styles.css (`.cm-afterimage-*`).
 */
import {
  type EditorState,
  type Extension,
  StateEffect,
  StateField,
  type TransactionSpec,
} from "@codemirror/state";
import { Decoration, type DecorationSet, EditorView } from "@codemirror/view";

const setFlash = StateEffect.define<number>();
const setHover = StateEffect.define<number | null>();

/** Two alternating classes, so flashing the same line again restarts the animation. */
const FLASH = [
  Decoration.line({ class: "cm-afterimage-flash-a" }),
  Decoration.line({ class: "cm-afterimage-flash-b" }),
];
const HOVER = Decoration.line({ class: "cm-afterimage-hover" });

/** The document position of a 1-based line, clamped to the document. */
function lineStart(state: EditorState, line: number): number {
  const { doc } = state;
  return doc.line(Math.min(Math.max(1, line), doc.lines)).from;
}

interface Highlights {
  flash: number | null;
  flashes: number;
  hover: number | null;
}

const highlights = StateField.define<Highlights>({
  create: () => ({ flash: null, flashes: 0, hover: null }),
  update(value, tr) {
    let next = value;
    for (const effect of tr.effects) {
      if (effect.is(setFlash)) next = { ...next, flash: effect.value, flashes: next.flashes + 1 };
      if (effect.is(setHover)) next = { ...next, hover: effect.value };
    }
    if (tr.docChanged) {
      const map = (pos: number | null) => (pos === null ? null : tr.changes.mapPos(pos));
      next = { ...next, flash: map(next.flash), hover: map(next.hover) };
    }
    return next;
  },
  provide: (field) =>
    EditorView.decorations.compute([field], (state): DecorationSet => {
      const { flash, flashes, hover } = state.field(field);
      const ranges = [];
      if (hover !== null) ranges.push(HOVER.range(state.doc.lineAt(hover).from));
      if (flash !== null) {
        ranges.push((FLASH[(flashes + 1) % 2] ?? HOVER).range(state.doc.lineAt(flash).from));
      }
      return Decoration.set(ranges, true);
    }),
});

export const lineHighlight = (): Extension => highlights;

/** Scrolls a 1-based line into view and flashes it. */
export function flashSpec(state: EditorState, line: number): TransactionSpec {
  const pos = lineStart(state, line);
  return { effects: [setFlash.of(pos), EditorView.scrollIntoView(pos, { y: "center" })] };
}

/** Softly highlights a 1-based line, or clears the highlight with null. */
export const hoverSpec = (state: EditorState, line: number | null): TransactionSpec => ({
  effects: setHover.of(line === null ? null : lineStart(state, line)),
});

/** The 1-based lines currently flashed and hovered (for tests). */
export function highlightedLines(state: EditorState): {
  flash: number | null;
  hover: number | null;
} {
  const { flash, hover } = state.field(highlights);
  const line = (pos: number | null) => (pos === null ? null : state.doc.lineAt(pos).number);
  return { flash: line(flash), hover: line(hover) };
}
